import { useEffect, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import type { RelationshipType, TypeCapabilityDto } from "../types";
import { formatLabel } from "../labels";

const RELATIONSHIP_TYPES: RelationshipType[] = [
  "owner",
  "manager",
  "staff",
  "coach",
  "trainer",
  "accountant",
  "consultant",
  "investor",
  "franchise_manager",
];

export interface CreateRelationshipDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    user_id: string;
    relationship_type: RelationshipType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function CreateRelationshipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateRelationshipDialogProps) {
  const [userId, setUserId] = useState("");
  const [relationshipType, setRelationshipType] =
    useState<RelationshipType>("staff");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add Business Relationship"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={userId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                user_id: userId.trim(),
                relationship_type: relationshipType,
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Relationships stay separate from Business status. Owner, Manager,
          Staff, Coach, Trainer, Accountant, Consultant, Investor, and Franchise
          Manager are distinct types.
        </Text>
        <Input
          label="User ID"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        />
        <Select
          label="Relationship type"
          value={relationshipType}
          onChange={(event) =>
            setRelationshipType(event.target.value as RelationshipType)
          }
          options={RELATIONSHIP_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export interface ReasonActionDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function ReasonActionDialog({
  open,
  title,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: ReasonActionDialogProps) {
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => void onSubmit(reason)}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export interface TransferOwnershipDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    new_owner_user_id: string;
    reason: string;
    approval_id: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function TransferOwnershipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: TransferOwnershipDialogProps) {
  const [newOwnerUserId, setNewOwnerUserId] = useState("");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Transfer ownership"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={
              newOwnerUserId.trim().length === 0 ||
              reason.trim().length < 3 ||
              approvalId.trim().length === 0
            }
            onClick={() =>
              void onSubmit({
                new_owner_user_id: newOwnerUserId.trim(),
                reason: reason.trim(),
                approval_id: approvalId.trim(),
              })
            }
          >
            Transfer
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Ownership transfer requires Manager approval. The backend remains
          authoritative.
        </Text>
        <Input
          label="New owner User ID"
          value={newOwnerUserId}
          onChange={(event) => setNewOwnerUserId(event.target.value)}
        />
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label="Approved Approval (required)"
          value={approvalId}
          onChange={setApprovalId}
          required
        />
        {error ? (
          <Alert tone="danger" title="Transfer failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export interface CreateCapabilityDialogProps {
  open: boolean;
  onClose: () => void;
  typeCapabilities: TypeCapabilityDto[];
  onSubmit: (input: { business_type_capability_id: string }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function CreateCapabilityDialog({
  open,
  onClose,
  typeCapabilities,
  onSubmit,
  busy = false,
  error = null,
}: CreateCapabilityDialogProps) {
  const [typeCapabilityId, setTypeCapabilityId] = useState(
    typeCapabilities[0]?.id ?? "",
  );

  useEffect(() => {
    if (open && !typeCapabilityId && typeCapabilities[0]) {
      setTypeCapabilityId(typeCapabilities[0].id);
    }
  }, [open, typeCapabilities, typeCapabilityId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add Business Capability"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!typeCapabilityId}
            onClick={() =>
              void onSubmit({ business_type_capability_id: typeCapabilityId })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Sports Facility is a Business Type. Facility operations run through
          eligible Business Capabilities, not as a capability themselves.
        </Text>
        {typeCapabilities.length === 0 ? (
          <Alert tone="warning" title="No type capabilities">
            This Business Type has no capability definitions, or they are not
            visible in the current scope.
          </Alert>
        ) : (
          <Select
            label="Type capability"
            value={typeCapabilityId}
            onChange={(event) => setTypeCapabilityId(event.target.value)}
            options={typeCapabilities.map((item) => ({
              value: item.id,
              label: `${formatLabel(item.capability_type)} (${item.status})`,
            }))}
          />
        )}
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
